import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import axios from 'axios';
import { TextInput } from '../../../../form/textInput';
import { Select } from '../../../../form/select';
import { updateNodeValue } from '../../../../../services/method';
import { operationType, selectDataType } from '../../../../../services/types';
import { Checkboxs } from '../../../../form/checkboxs';
import { Main, Styled } from './style';
import { Switch } from '../../../../form/switch';
import { AuthenticateSelect } from '../../../../auth/select';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';

type TrelloProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

let reqStatus = { boards: false, lists: false };

export function Trello({ id, data, setChildMinimize, setChildSettings }: TrelloProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Fetch Attachments', refId: 'fetch_attachments', isActive: true }, 
      { id: 2, name: 'Fetch Cards', refId: 'fetch_cards' }, { id: 3, name: 'Create Card', refId: 'create_card' },
      { id: 4, name: 'Let AI Agent Decide', refId: 'agent_decide' }
   ]);
   const { setNodes } = useReactFlow();
   const credential = { headers: { Authorization: `Bearer ${data.value?.connection?.token}` }};
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         if (data.currentCategory === 'Actions') {
            setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === 'create_card' })));
            setNodes(nodes => nodes.map(node => {
               if (node.id === id) {
                  return { ...node, data: { ...node.data, value: { ...node.data.value, 
                     request: 'create_card', boards: [], lists: [], cards: []
                  }}}
               } else return node;
            }));
         } else updateNodeValue(id, setNodes, { request:  requests[0].refId });
      } else {
         setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   React.useEffect(() => {
      if (reqStatus.boards && data.value?.connection && data.value.boards.length) {
         const fetchData = async () => {
            reqStatus.boards = false;
            const boardId = data.value.boards.find((item: any) => item.isActive).id;
            const lists = (await axios.get(`/project_management/Trello/boards/${boardId}/lists`, credential)).data;
            updateNodeValue(id, setNodes, { lists: lists.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
         }
         fetchData();
      } // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.boards]);

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: { id: string, token: string }) => {
      if (type === 'add') {
         const boards = (await axios.get('/project_management/Trello/boards', { headers: { Authorization: `Bearer ${data.token}`}})).data;
         reqStatus.boards = true;
         updateNodeValue(id, setNodes, { connection: data, boards: boards.map((item: any, index: number) => ({ id: item.board_id, name: item.board_name, isActive: index === 0 })) });   
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }
   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   }

   // ---
   const handleSelectRequests = (action: 'request' | 'boards' | 'lists', selectId: number) => {
      if (action === 'request') {
         const refId = requests.find(obj => obj.id === selectId)?.refId;

         setRequests((requests) => requests.map((request) => {
            if (request.id === selectId) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));
         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId }}}
            } else return node;
         }));

      } else if (action === 'boards' || action === 'lists') {
         const arr = data.value[action].map((item: any) => ({ ...item, isActive: item.id === selectId })); // @ts-ignore
         reqStatus[action] = true;
         updateNodeValue(id, setNodes, { [action]: arr });
      }
   }

   // ---
   const handleButtons = async (type: 'attachments' | 'open') => {
      try {
         if (type === 'attachments') {
            updateNodeValue(id, setNodes, { isLoading: true });
            const boardId = data.value.boards.find((item: any) => item.isActive).id;
            const response = (await axios.get(`/project_management/Trello/boards/${boardId}/fetch_attachments`, credential)).data;
            updateNodeValue(id, setNodes, { isLoading: false, attachments: response.attachments.map((file: any) => ({ id: file.id, name: file.filename, type: file.mime_type, url: file.url }))});
          
         } else if (type === 'open') {
            window.open(data.value.attachments.find((file: any) => file.isActive).url, '_blank');
         }
      } catch {
         updateNodeValue(id, setNodes, { isLoading: false });
      }
   }

   // ---
   const attachmentsLengthActive = data.value?.request === 'fetch_attachments' ? data.value?.attachments?.filter((item: any) => item.isActive)?.length : 0;
   const isWarning = !data.value?.connection || !data.value?.boards.length;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            styleParent={ (data.value?.request === 'fetch_attachments' && data.value?.attachments?.length ? { right: '-101.5px' } : {}) }
            style={{ width: '420px' }}
         ><Styled>
            <AuthenticateSelect type="trello" defaultId={ data.value?.connection?.id } handleAuthenticate={ handleAuthenticate } background={ data.background } />
            <div className="action">
               { data.value?.connection && data.value?.request === 'fetch_attachments' ? <>
                  <button className="btn1 withBorder" onClick={ _=> handleButtons('attachments') } disabled={ data.value?.isLoading }>Fetch</button> 
                  { attachmentsLengthActive === 1 ? <button className="btn1 withBorder open" onClick={ _=> handleButtons('open') } disabled={ data.value?.isLoading }>Open</button> : null }
               </> : null }
            </div>
            <Select title="Request" data={ requests } handleSelectItem={ (id) => handleSelectRequests('request', id) } isSmall={ true } />
            <Select title="Board Id" data={ data.value?.boards } handleSelectItem={ (id) => handleSelectRequests('boards', id) } isSmall={ true } />
            { data.value?.request !== 'fetch_attachments' ? <Select title="List Id" data={ data.value?.lists } handleSelectItem={ (id) => handleSelectRequests('lists', id) } isSmall={ true } /> : null }
            { data.value?.request === 'create_card' ? <>
               <TextInput keyRef="card_name" value={ data.value?.card_name } title="Name" placeholder="card_name" handleChange={ handleChangeInput } /> 
            </> : null }
            { data.value?.request === 'fetch_attachments' ? <>
               { data.value?.attachments?.length ? <div className="filesEdit attachments">
                  <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Attachments ({ attachmentsLengthActive }/{ data.value?.attachments?.length })</h3>
                  <div className="scroll nowheel">
                     <Checkboxs 
                        data={ data.value.attachments } 
                        handelCheckbox={ (fileId: any, value?: boolean) => { 
                           updateNodeValue(id, setNodes, { attachments: data.value.attachments.map((item: any) => {
                              if (item.id === fileId) return ({ ...item, isActive: !value })
                              else return item;
                           })});
                        }}
                     />
                  </div>
               </div> : null }
               <Switch title="Start autopilot (with loop)" className="autopilot" value={ !!data.value?.isAutopilot } setValue={ () => updateNodeValue(id, setNodes, { isAutopilot: !data.value?.isAutopilot }) } />
               <p>Please select your keywords carefully to avoid downloading incorrect or unintended files!</p> 
            </>: null}
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}